import { Badge, Card, EmptyState, Panel } from '@hgc/ui';
import type { JSX } from 'react';

/** 分区标题。四个分区共用，保证"我在哪、这块是干什么的"永远在同一个位置 */
export function SectionHeader({
  title,
  summary,
}: {
  readonly title: string;
  readonly summary: string;
}): JSX.Element {
  return (
    <header className="flex flex-col gap-0.5">
      <h1 className="m-0 text-base font-semibold text-fg">{title}</h1>
      <p className="m-0 max-w-3xl text-xs text-fg-muted">{summary}</p>
    </header>
  );
}

export interface PlannedItem {
  /** 这个分区**将来会提供什么** */
  readonly title: string;
  /** 由谁提供（todo 名或包名）—— 让"为什么现在是空的"有一个可追踪的答案 */
  readonly owner: string;
}

/**
 * "后面会接到这里"的清单。
 *
 * 存在的理由：一个空面板如果不说清"谁、什么时候、为什么现在没有"，用户唯一的结论是
 * "这个软件坏了"。同理，它也拒绝了另一种做法 —— 用假数据把界面填满：
 * 假数据会让"接线到底通没通"变得无法判断，而那正是本阶段要验证的唯一一件事。
 */
export function PlannedWorkCard({ items }: { readonly items: readonly PlannedItem[] }): JSX.Element {
  return (
    <Card title="后续接入">
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {items.map((item) => (
          <li key={item.title} className="flex items-start gap-2">
            <span className="min-w-0 flex-1 text-xs text-fg">{item.title}</span>
            <Badge size="sm" variant="outline" level="info">
              {item.owner}
            </Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * 还没有内容的分区。
 *
 * `EmptyState` 的 `title` 必须回答"**为什么**这里是空的"，`description` 补上细节，
 * 这里两者都由调用方显式给出 —— 不允许出现"列表为空"这种等于没说的空状态。
 *
 * 用 `Panel` 而不是 `Card` 包住空状态：Panel 承担的是"页面上的一块区域"，
 * 之后的航点列表、参数表会直接长在这个区域里（`fill` + `scroll` 到那时才有意义，
 * 现在内容高度自然，给了反而会多出一层无谓的滚动容器）。
 */
export function SectionPlaceholder({
  title,
  summary,
  emptyTitle,
  emptyReason,
  items,
}: {
  readonly title: string;
  readonly summary: string;
  readonly emptyTitle: string;
  readonly emptyReason: string;
  readonly items: readonly PlannedItem[];
}): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <SectionHeader title={title} summary={summary} />
      <Panel title="为什么现在是空的">
        <EmptyState level="info" title={emptyTitle} description={emptyReason} />
      </Panel>
      <PlannedWorkCard items={items} />
    </div>
  );
}
